// Map a database PollRow into the public dashboard's Poll shape.
//
// The DB schema deliberately uses richer column names (poll_date, pl_pct,
// fieldwork_window, ...) and a different pollster taxonomy
// (`pollster_grouped`: 'MaltaToday' | 'Sagalytics/Marmarà' | 'Times of
// Malta' | 'Other'). The existing dashboard components use the legacy
// `Poll` shape with `PollsterName` (a 4-value union including the EP
// anchor). This file is the bridge.

import type { Poll, PollsterName, OppLeader } from '../data/surveys.types';
import type { PollRow, PollsterGrouped } from '../db/types';

const GROUPED_TO_LEGACY: Record<PollsterGrouped, PollsterName> = {
  MaltaToday: 'MaltaToday',
  'Sagalytics/Marmarà': 'Sagalytics',
  'Times of Malta': 'Esprimi (Times of Malta)',
  // "Other" pollsters fall through to Esprimi for the legacy components
  // that key on the 4-name union. Once the dashboard gains real "Other"
  // styling we can drop this.
  Other: 'Esprimi (Times of Malta)',
};

function legacyPollster(row: PollRow): PollsterName {
  if (row.is_election_anchor) return '2024 EP Election (actual result)';
  return GROUPED_TO_LEGACY[row.pollster_grouped] ?? 'Esprimi (Times of Malta)';
}

function legacyOppName(name: string | null): OppLeader {
  if (name === 'Grech' || name === 'Borg') return name;
  // Default: treat unknown names as "Borg" since that's the current
  // opposition leader. This only affects the opposition-trust label
  // colouring on the inspection table; the underlying value stays.
  return 'Borg';
}

export function rowToPoll(row: PollRow): Poll {
  const lead =
    row.lead != null
      ? row.lead
      : row.pl_pct != null && row.pn_pct != null
        ? row.pl_pct - row.pn_pct
        : 0;

  const rawLead =
    row.raw_pl_pct != null && row.raw_pn_pct != null
      ? row.raw_pl_pct - row.raw_pn_pct
      : undefined;

  return {
    date: row.poll_date.slice(0, 10),
    pollster: legacyPollster(row),
    sample: row.sample_size,
    pl: row.pl_pct,
    pn: row.pn_pct,
    adpd: row.adpd_pct,
    mom: row.mom_pct,
    other: row.other_pct,
    raw_pl: row.raw_pl_pct,
    raw_pn: row.raw_pn_pct,
    turnout: row.turnout_pct,
    abela: row.abela_trust,
    opp: row.opp_trust,
    opp_name: legacyOppName(row.opp_name),
    none: row.neither_pct,
    gov_perf: row.gov_perf,
    field: row.fieldwork_window ?? '',
    url: row.source_url ?? '',
    notes: row.notes ?? '',
    lead,
    ...(rawLead !== undefined ? { raw_lead: rawLead } : {}),
  };
}
